// Alle Edit-Befehle an einer Stelle: Name, Gruppe, Tastenkürzel (wie Pro Tools, soweit der Browser es zulässt),
// Ausführung. Das Bearbeiten-Menü, das Kontextmenü der Clips und die Tastatur nutzen diese Liste.
import type { Clip, Project } from '@srs/shared';
import { beatAt } from '@srs/shared';
import { useStudio, projectLength } from '../state/store.ts';
import { useEdit } from '../state/edit.ts';
import { engine } from '../audio/engine.ts';
import { toast } from '../components/ui.tsx';
import { uid } from '../state/demo.ts';
import { api } from '../api/client.ts';
import { encodeWav } from '../audio/recorder.ts';
import { clipPeak } from './analysis.ts';
import * as op from './ops.ts';
import * as A from './automation.ts';
import * as mix from './mixops.ts';
import { clipTransients } from '../audio/events.ts';
import { beatConform, beatSmooth, classify, detectRange } from './beat.ts';

/** Auswahl liegt in Automation-Lanes? Dann die betroffenen Kurven: [trackId, param] */
function autoTargets(): [string, string][] {
  const r = ed().range;
  if (!r) return [];
  return r.trackIds.filter((id) => id.startsWith('auto:')).map((id) => { const [, tid, ...rest] = id.split(':'); return [tid, rest.join(':')] as [string, string]; });
}
function autoTrim(delta: number) {
  const r = ed().range!, list = autoTargets();
  st().commit('Automation trimmen', (p) => { for (const [tid, param] of list) { const t = A.trackLike(p, tid); if (!t) continue; const info = A.paramInfo(param, t); A.trimRange(t, param, r.start, r.end, info.unit === 'dB' ? delta : info.step || info.choices ? Math.sign(delta) : (info.max - info.min) * 0.02 * delta); } }, { merge: true });
}

export interface Command {
  id: string; label: string; group: string;
  /** Kürzel, z. B. 'mod+x', 'alt+r', 'b', 'shift+mod+e'. mod = ⌘ (Mac) bzw. Strg. */
  keys?: string[];
  run(): void | Promise<void>;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const st = () => useStudio.getState();
const ed = () => useEdit.getState();
const shuffle = () => ed().mode === 'shuffle';
const srcLen: op.SrcLen = (id) => engine.buffers.get(id)?.duration;
const commit = (label: string, fn: (p: Project) => void) => st().commit(label, fn);

/** aktuelle Auswahl; ohne Auswahl die ausgewählte Spur an der Abspielposition */
export function currentRange(): op.Range | null {
  const r = ed().range;
  if (r && r.trackIds.length) return r;
  const s = st();
  const clips = s.project.clips.filter((c) => s.selection.clipIds.includes(c.id));
  if (clips.length) return { start: Math.min(...clips.map((c) => c.start)), end: Math.max(...clips.map(op.clipEnd)), trackIds: [...new Set(clips.map((c) => c.trackId))] };
  if (s.selection.trackId) return { start: engine.position, end: engine.position, trackIds: [s.selection.trackId] };
  return null;
}
/** betroffene Clips: ausgewählte Clips, sonst alle in der Auswahl, sonst die unter der Einfügemarke */
export function targetClips(p = st().project): Clip[] {
  const ids = st().selection.clipIds;
  if (ids.length) return p.clips.filter((c) => ids.includes(c.id));
  const r = currentRange();
  if (!r) return [];
  return p.clips.filter((c) => r.trackIds.includes(c.trackId) && (op.isEmptyRange(r) ? r.start >= c.start && r.start < op.clipEnd(c) : c.start < r.end && op.clipEnd(c) > r.start));
}
const need = (r: op.Range | null, range = false): r is op.Range => {
  if (!r) { toast('Erst eine Auswahl treffen oder eine Spur anklicken'); return false; }
  if (range && op.isEmptyRange(r)) { toast('Dafür einen Bereich auswählen (mit dem Selector ziehen)'); return false; }
  return true;
};
const setRange = (r: op.Range | null) => { ed().setRange(r); st().select([], r?.trackIds[0] ?? null); if (r && !st().transport.playing) st().setTransport({ position: r.start }); };
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

// ---------------------------------------------------------------------------
async function consolidate() {
  const r = currentRange();
  if (!need(r, true)) return;
  const p = st().project;
  for (const tid of r.trackIds) {
    const t = p.tracks.find((x) => x.id === tid);
    if (!t) continue;
    if (t.kind === 'midi') {
      commit('Konsolidieren', (q) => {
        const cb = op.copyRange(q, { ...r, trackIds: [tid] });
        op.clearRange(q, { ...r, trackIds: [tid] }, false);
        const b0 = beatAt(q, r.start);
        q.clips.push({ id: uid('c'), trackId: tid, kind: 'midi', name: t.name, start: r.start, offset: 0, duration: r.end - r.start, fadeIn: 0, fadeOut: 0, by: { userId: st().user.id, at: new Date().toISOString() },
          notes: cb.tracks[0].flatMap((c) => { const cs = beatAt(q, r.start + c.start) - b0, len = beatAt(q, r.start + c.start + c.duration) - b0 - cs; return (c.notes ?? []).filter((n) => n.t >= 0 && n.t < len - 1e-9).map((n) => ({ ...n, t: n.t + cs })); }) });
      });
      continue;
    }
    const buf = await engine.renderTrackRange(p, tid, r.start, r.end);
    const sourceId = uid('src');
    engine.buffers.set(sourceId, buf);
    commit('Konsolidieren', (q) => {
      op.clearRange(q, { ...r, trackIds: [tid] }, false);
      q.clips.push({ id: uid('c'), trackId: tid, kind: 'audio', name: `${t.name} konsolidiert`, start: r.start, offset: 0, duration: r.end - r.start, sourceId, fadeIn: 0, fadeOut: 0, by: { userId: st().user.id, at: new Date().toISOString() } });
    });
    if (st().token) void api.uploadWav(encodeWav(buf), `${sourceId}.wav`, p.id).then((m) => { if (m) { engine.buffers.set(m.id, buf); st().replaceSourceId(sourceId, m.id); } }).catch(() => toast('Konsolidierte Datei konnte nicht hochgeladen werden, sie klingt nur bei dir.'));
  }
  toast('Konsolidiert: ein Clip je Spur');
}

function normalizeClips() {
  const clips = targetClips().filter((c) => c.kind === 'audio');
  if (!clips.length) return toast('Keine Audio-Clips ausgewählt');
  commit('Normalisieren', (p) => op.normalize(p, clips.map((c) => c.id), (c) => {
    const b = c.sourceId ? engine.buffers.get(c.sourceId) : undefined;
    return b ? clipPeak(Array.from({ length: b.numberOfChannels }, (_, i) => b.getChannelData(i)), b.sampleRate, c) : 0;
  }));
  toast(`${plural(clips.length, 'Clip', 'Clips')} auf −0,1 dBFS normalisiert (über Clip Gain, das Original bleibt unverändert)`);
}

function tab(dir: 1 | -1, extend = false) {
  const s = st(), r = currentRange();
  const tracks = r?.trackIds ?? (s.selection.trackId ? [s.selection.trackId] : s.project.tracks.map((t) => t.id));
  const at = r ? (dir > 0 ? r.end : r.start) : engine.position;
  // Tab to Transient: zusätzlich zu den Clip-Grenzen die erkannten Anschläge der Audio-Clips
  const clips = s.project.clips.filter((c) => tracks.includes(c.trackId));
  const pts = clips.flatMap((c) => [c.start, op.clipEnd(c), ...(ed().tabTransient ? (clipTransients(s.project, c, true) ?? []).map((t) => c.start + t) : [])]);
  const next = dir > 0 ? Math.min(...pts.filter((x) => x > at + 1e-6)) : Math.max(...pts.filter((x) => x < at - 1e-6), 0);
  if (!Number.isFinite(next)) return;
  if (extend && r) setRange(dir > 0 ? { ...r, end: next } : { ...r, start: next });
  else { setRange({ start: next, end: next, trackIds: tracks }); engine.locate(next); }
}

function addMarker() {
  const r = ed().range, p = st().project;
  const at = r && !op.isEmptyRange(r) ? r.start : engine.position;
  const n = p.markers.length + 1;
  commit('Marker', (q) => { q.markers.push({ id: uid('m'), name: r && !op.isEmptyRange(r) ? `Bereich ${n}` : `Marker ${n}`, time: at, end: r && !op.isEmptyRange(r) ? r.end : undefined }); q.markers.sort((a, b) => a.time - b.time); });
  toast(`Marker ${n} gesetzt. Doppelklick auf den Marker im Lineal benennt ihn um.`);
}

function zoomTo(a: number, b: number) {
  const w = (document.querySelector('.canvaswrap') as HTMLElement | null)?.clientWidth ?? 1000;
  st().setZoom((w * 0.92) / Math.max(0.5, b - a));
  window.dispatchEvent(new CustomEvent('srs:scrollTo', { detail: Math.max(0, a - (b - a) * 0.04) }));
}

export const COMMANDS: Command[] = [
  // Bearbeiten
  { id: 'undo', label: 'Rückgängig', group: 'Bearbeiten', keys: ['mod+z'], run: () => st().undo() },
  { id: 'redo', label: 'Wiederholen', group: 'Bearbeiten', keys: ['shift+mod+z'], run: () => st().redo() },
  { id: 'cut', label: 'Ausschneiden', group: 'Bearbeiten', keys: ['mod+x'], run: () => {
    const r = currentRange(); if (!need(r, true)) return;
    ed().set({ clipboard: op.copyRange(st().project, r) });
    commit('Ausschneiden', (p) => op.clearRange(p, r, shuffle()));
    setRange({ ...r, end: r.start });
  } },
  { id: 'copy', label: 'Kopieren', group: 'Bearbeiten', keys: ['mod+c'], run: () => {
    const r = currentRange(); if (!need(r, true)) return;
    const cb = op.copyRange(st().project, r);
    ed().set({ clipboard: cb });
    toast(`Kopiert: ${plural(cb.tracks.flat().length, 'Clip', 'Clips')}`);
  } },
  { id: 'paste', label: 'Einfügen', group: 'Bearbeiten', keys: ['mod+v'], run: () => {
    const r = currentRange(), cb = ed().clipboard;
    if (!cb) return toast('Zwischenablage ist leer');
    if (!need(r)) return;
    let sel: op.Range = r;
    commit('Einfügen', (p) => { sel = op.paste(p, cb, r, shuffle()); });
    setRange(sel);
  } },
  { id: 'clear', label: 'Löschen', group: 'Bearbeiten', keys: ['mod+b', 'backspace', 'delete'], run: () => {
    const ids = st().selection.clipIds, r = ed().range;
    const at = autoTargets();
    if (at.length && r && !op.isEmptyRange(r)) { commit('Automation löschen', (p) => { for (const [tid, param] of at) { const t = A.trackLike(p, tid); if (t) A.removeRange(t, param, r.start, r.end, false); } }); return; }
    if (r && !op.isEmptyRange(r)) { commit('Löschen', (p) => op.clearRange(p, r, shuffle())); setRange({ ...r, end: r.start }); return; }
    if (ids.length) {
      commit('Clips löschen', (p) => {
        const gone = p.clips.filter((c) => ids.includes(c.id));
        p.clips = p.clips.filter((c) => !ids.includes(c.id));
        if (shuffle()) for (const g of gone) for (const c of p.clips) if (c.trackId === g.trackId && c.start >= op.clipEnd(g) - 1e-6) c.start -= g.duration;
      });
      st().select([]);
    }
  } },
  { id: 'fill', label: 'Repeat to Fill Selection', group: 'Bearbeiten', keys: ['alt+mod+v'], run: () => {
    const r = currentRange(), cb = ed().clipboard;
    if (!cb) return toast('Erst etwas kopieren');
    if (!need(r, true)) return;
    commit('Auswahl füllen', (p) => op.repeatToFill(p, cb, r));
  } },
  { id: 'duplicate', label: 'Duplizieren', group: 'Bearbeiten', keys: ['mod+d'], run: () => {
    const r = currentRange(); if (!need(r, true)) return;
    let sel = r; commit('Duplizieren', (p) => { sel = op.duplicate(p, r, shuffle()); }); setRange(sel);
  } },
  { id: 'repeat', label: 'Wiederholen …', group: 'Bearbeiten', keys: ['alt+r'], run: () => { if (need(currentRange(), true)) ed().open('repeat'); } },
  { id: 'selectAll', label: 'Alles auswählen', group: 'Bearbeiten', keys: ['mod+a'], run: () => {
    const p = st().project;
    setRange({ start: 0, end: Math.max(0, ...p.clips.map(op.clipEnd)), trackIds: p.tracks.map((t) => t.id) });
  } },
  // Clips teilen und kürzen
  { id: 'separate', label: 'Teilen (Separate)', group: 'Schneiden', keys: ['mod+e', 'b'], run: () => {
    const r = currentRange(); if (!need(r)) return;
    commit('Teilen', (p) => { op.splitTracksAt(p, r.trackIds, r.start); if (!op.isEmptyRange(r)) op.splitTracksAt(p, r.trackIds, r.end); });
  } },
  { id: 'heal', label: 'Heal Separation', group: 'Schneiden', keys: ['h'], run: () => {
    const r = currentRange(); if (!need(r)) return;
    let n = 0; commit('Heal', (p) => { n = op.heal(p, op.isEmptyRange(r) ? { ...r, start: r.start - 0.001, end: r.end + 0.001 } : r); });
    toast(n ? `${plural(n, 'Schnitt', 'Schnitte')} geheilt` : 'Nichts zu heilen: Die Teile stammen nicht lückenlos aus demselben Material');
  } },
  { id: 'trimSel', label: 'Clip auf Auswahl kürzen', group: 'Schneiden', keys: ['alt+t'], run: () => { const r = currentRange(); if (need(r, true)) commit('Auf Auswahl kürzen', (p) => op.trimToRange(p, r)); } },
  { id: 'trimStart', label: 'Anfang bis Einfügemarke kürzen', group: 'Schneiden', keys: ['a'], run: () => { const r = currentRange(); if (need(r)) commit('Anfang kürzen', (p) => op.trimStartTo(p, r.trackIds, r.start)); } },
  { id: 'trimEnd', label: 'Ende ab Einfügemarke kürzen', group: 'Schneiden', keys: ['s'], run: () => { const r = currentRange(); if (need(r)) commit('Ende kürzen', (p) => op.trimEndTo(p, r.trackIds, r.start)); } },
  { id: 'insertSilence', label: 'Stille einfügen', group: 'Schneiden', keys: ['shift+mod+e'], run: () => { const r = currentRange(); if (need(r, true)) commit('Stille einfügen', (p) => op.insertSilence(p, r)); } },
  { id: 'tempoDialog', label: 'Tempo und Taktart …', group: 'Marker und Zeit', keys: ['alt+shift+t'], run: () => ed().open('tempo', String(engine.position)) },
  { id: 'audiosuite', label: 'AudioSuite …', group: 'Clip', keys: ['alt+shift+u'], run: () => { if (targetClips().some((c) => c.kind === 'audio') || need(currentRange(), true)) ed().open('audiosuite'); } },
  { id: 'pitch', label: 'Tonhöhe …', group: 'Clip', run: () => { if (targetClips().some((c) => c.kind === 'audio') || need(currentRange(), true)) ed().open('audiosuite', 'pitch'); } },
  { id: 'equant', label: 'Audio quantisieren (Elastic) …', group: 'Schneiden', run: () => { if (need(currentRange(), true)) ed().open('equant'); } },
  { id: 'beat', label: 'Beat Detective …', group: 'Schneiden', keys: ['mod+8'], run: () => { if (need(currentRange(), true)) ed().open('beat'); } },
  { id: 'strip', label: 'Strip Silence …', group: 'Schneiden', keys: ['mod+u'], run: () => { if (need(currentRange(), true)) ed().open('strip'); } },
  { id: 'consolidate', label: 'Konsolidieren', group: 'Schneiden', keys: ['alt+shift+3'], run: consolidate },
  // Fades
  { id: 'fades', label: 'Fades …', group: 'Fades', keys: ['mod+f', 'f'], run: () => { if (need(currentRange(), true)) ed().open('fades'); } },
  { id: 'fadeIn', label: 'Fade-In bis Einfügemarke', group: 'Fades', keys: ['d'], run: () => { const r = currentRange(); if (need(r)) commit('Fade-In', (p) => op.fadeTo(p, r.trackIds, r.start, 'in', ed().fades.inShape)); } },
  { id: 'fadeOut', label: 'Fade-Out ab Einfügemarke', group: 'Fades', keys: ['g'], run: () => { const r = currentRange(); if (need(r)) commit('Fade-Out', (p) => op.fadeTo(p, r.trackIds, r.start, 'out', ed().fades.outShape)); } },
  { id: 'fadesRemove', label: 'Fades in der Auswahl entfernen', group: 'Fades', run: () => { const r = currentRange(); if (need(r, true)) commit('Fades entfernen', (p) => op.removeFades(p, r)); } },
  // Clip
  { id: 'mute', label: 'Clip stumm/laut', group: 'Clip', keys: ['ctrl+m'], run: () => { const ids = targetClips().map((c) => c.id); if (!ids.length) return toast('Keinen Clip ausgewählt'); let m = false; commit('Clip stumm', (p) => { m = op.toggleMute(p, ids); }); toast(m ? 'Clip stumm' : 'Clip wieder hörbar'); } },
  { id: 'lock', label: 'Clip sperren (Time Lock)', group: 'Clip', keys: ['alt+ctrl+l'], run: () => { const ids = targetClips().map((c) => c.id); if (!ids.length) return toast('Keinen Clip ausgewählt'); let l = false; commit('Time Lock', (p) => { l = op.toggleLock(p, ids); }); toast(l ? 'Gesperrt: lässt sich nicht mehr verschieben' : 'Entsperrt'); } },
  { id: 'loop', label: 'Clip loopen …', group: 'Clip', keys: ['alt+mod+l'], run: () => { const c = targetClips()[0]; if (!c) return toast('Keinen Clip ausgewählt'); ed().open('loop', c.id); } },
  { id: 'unloop', label: 'Loop auflösen', group: 'Clip', run: () => { const ids = targetClips().filter((c) => c.loop).map((c) => c.id); if (!ids.length) return toast('Kein geloopter Clip ausgewählt'); commit('Loop auflösen', (p) => ids.forEach((id) => op.unloopClip(p, id))); } },
  { id: 'group', label: 'Clips gruppieren', group: 'Clip', keys: ['alt+mod+g'], run: () => { const ids = targetClips().map((c) => c.id); if (ids.length < 2) return toast('Mindestens zwei Clips auswählen (⇧-Klick)'); commit('Gruppieren', (p) => op.groupClips(p, ids)); toast(`${ids.length} Clips gruppiert: Sie werden zusammen ausgewählt und bewegt`); } },
  { id: 'ungroup', label: 'Gruppe auflösen', group: 'Clip', keys: ['alt+mod+u'], run: () => { const ids = targetClips().map((c) => c.id); let n = 0; commit('Gruppe auflösen', (p) => { n = op.ungroupClips(p, ids); }); toast(n ? 'Gruppe aufgelöst' : 'Keine Gruppe ausgewählt'); } },
  { id: 'gainUp', label: 'Clip Gain bzw. Automation +1 dB', group: 'Clip', keys: ['ctrl+shift+arrowup'], run: () => { if (autoTargets().length && ed().range && !op.isEmptyRange(ed().range!)) return autoTrim(1); const ids = targetClips().map((c) => c.id); if (ids.length) st().commit('Clip Gain', (p) => op.setClipGain(p, ids, 1, true), { merge: true }); } },
  { id: 'gainDown', label: 'Clip Gain bzw. Automation −1 dB', group: 'Clip', keys: ['ctrl+shift+arrowdown'], run: () => { if (autoTargets().length && ed().range && !op.isEmptyRange(ed().range!)) return autoTrim(-1); const ids = targetClips().map((c) => c.id); if (ids.length) st().commit('Clip Gain', (p) => op.setClipGain(p, ids, -1, true), { merge: true }); } },
  { id: 'gain', label: 'Clip Gain …', group: 'Clip', run: () => { if (targetClips().length) ed().open('gain'); else toast('Keinen Clip ausgewählt'); } },
  { id: 'normalize', label: 'Normalisieren', group: 'Clip', run: normalizeClips },
  { id: 'deleteTake', label: 'Take löschen', group: 'Clip', run: () => {
    const p = st().project, ids = [...new Set(targetClips().map((c) => c.takeId).filter((x): x is string => !!x && p.takes.some((t) => t.id === x)))];
    if (!ids.length) return toast('Kein aufgenommener Take ausgewählt');
    const ns = ids.map((id) => p.takes.find((t) => t.id === id)!.n);
    removeTakes(ids, ids.length === 1 ? `Take ${ns[0]}` : `Takes ${ns.join(', ')}`);
  } },
  { id: 'quantizeClips', label: 'Clips aufs Raster (am Anschlag)', group: 'Clip', keys: ['alt+q'], run: () => {
    const p = st().project, cl = targetClips(p), ids = cl.map((c) => c.id);
    if (!ids.length) return toast('Keinen Clip ausgewählt');
    // Anker je Clip vorher bestimmen: Sync Point, sonst erster Anschlag kurz nach dem Anfang
    const anchors = new Map(cl.map((c) => [c.id, op.syncOf(c, clipTransients(p, c, true))]));
    let n = 0;
    commit('Clips aufs Raster', (q) => { n = op.quantizeClips(q, ids, ed().grid, { anchor: (c) => anchors.get(c.id) ?? 0 }); });
    toast(`${n} ${n === 1 ? 'Clip' : 'Clips'} mit dem Anschlag aufs Raster ${op.GRIDS.find((g) => g.id === ed().grid)?.label ?? ''} gezogen`);
  } },
  { id: 'beatAll', label: 'Anschläge freischneiden und aufs Raster', group: 'Clip', keys: ['alt+mod+8'], run: () => {
    // Beat Detective in einem Schritt: erkennen, an jedem Anschlag teilen, aufs Raster ziehen, Lücken füllen, Crossfades
    const r = currentRange();
    if (!r || op.isEmptyRange(r)) return toast('Erst einen Bereich oder Clips auswählen (z. B. den Bass über ein paar Takte)');
    const p = st().project, E = ed(), grid = op.GRIDS.some((g) => g.id === E.grid && g.beats > 0) ? E.grid : '1/16';
    const o = { ...E.beat, resolution: 'sub' as const, sub: grid };
    const det = detectRange(p, r, (id) => engine.buffers.get(id), o.emphasis);
    const triggers = classify(p, det.onsets, o);
    if (!triggers.length) return toast('Keine Anschläge gefunden. Beat Detective (⌘8) zeigt die Erkennung mit Empfindlichkeit.');
    let n = 0;
    commit('Anschläge aufs Raster', (q) => { n = beatConform(q, r, triggers, o); beatSmooth(q, r, o, (id) => engine.buffers.get(id)?.duration); });
    toast(`${triggers.length} Anschläge freigeschnitten, ${n} Stücke aufs Raster ${op.GRIDS.find((g) => g.id === grid)?.label} gezogen, Lücken gefüllt. Feinarbeit: Beat Detective (⌘8). Rückgängig mit ⌘Z.`, 6000);
  } },
  { id: 'vtiming', label: 'Vocal-Timing …', group: 'Clip', keys: ['alt+shift+v'], run: () => ed().open('vtiming') },
  { id: 'syncSet', label: 'Sync Point an der Einfügemarke', group: 'Clip', keys: ['mod+,'], run: () => {
    const p = st().project, r = currentRange(), at = r ? r.start : engine.position;
    const cl = targetClips(p).filter((c) => c.kind === 'audio' && at > c.start && at < op.clipEnd(c));
    if (!cl.length) return toast('Einfügemarke in einen Audio-Clip setzen');
    commit('Sync Point', (q) => { for (const c of q.clips) if (cl.some((x) => x.id === c.id)) c.syncPoint = at - c.start; });
    toast('Sync Point gesetzt: im Grid-Modus rastet diese Stelle aufs Raster ein');
  } },
  { id: 'syncClear', label: 'Sync Point entfernen', group: 'Clip', run: () => { const ids = targetClips().map((c) => c.id); commit('Sync Point entfernen', (q) => { for (const c of q.clips) if (ids.includes(c.id)) delete c.syncPoint; }); } },
  { id: 'events', label: 'Event Operations (MIDI) …', group: 'Clip', keys: ['alt+0'], run: () => { if (targetClips().some((c) => c.kind === 'midi')) ed().open('events'); else toast('Erst MIDI-Clips auswählen'); } },
  { id: 'rename', label: 'Umbenennen …', group: 'Clip', run: () => { if (targetClips().length) ed().open('rename'); else toast('Keinen Clip ausgewählt'); } },
  { id: 'spot', label: 'Spotten (Position eingeben) …', group: 'Clip', run: () => { const c = targetClips()[0]; if (c) ed().open('spot', c.id); else toast('Keinen Clip ausgewählt'); } },
  { id: 'nudgeL', label: 'Nudge zurück', group: 'Clip', keys: [','], run: () => nudgeBy(-1) },
  { id: 'nudgeR', label: 'Nudge vor', group: 'Clip', keys: ['.'], run: () => nudgeBy(1) },
  { id: 'comp', label: 'Auswahl in die Haupt-Playlist kopieren', group: 'Clip', keys: ['alt+ctrl+v'], run: () => window.dispatchEvent(new CustomEvent('srs:comp')) },
  // Automation
  { id: 'autoShow', label: 'Automation der ausgewählten Spur zeigen', group: 'Automation', keys: ['alt+shift+a'], run: () => {
    const tid = st().selection.trackId ?? currentRange()?.trackIds.find((x) => !x.includes(':'));
    if (!tid) return toast('Erst eine Spur anklicken');
    const t = st().project.tracks.find((x) => x.id === tid)!;
    const used = Object.entries(t.automation?.lanes ?? {}).filter(([, l]) => l.length).map(([k]) => k);
    ed().set({ autoLanes: { ...ed().autoLanes, [tid]: (ed().autoLanes[tid]?.length ?? 0) ? [] : used.length ? used : ['volume'] } });
  } },
  { id: 'export', label: 'Export: Mixdown und Stems …', group: 'Spur', keys: ['alt+mod+b'], run: () => ed().open('export') },
  { id: 'commit', label: 'Commit: Spur auf neue Audiospur …', group: 'Spur', run: () => { const ids = selectedTrackIds().filter((id) => st().project.tracks.some((t) => t.id === id)); if (!ids.length) return toast('Erst eine Spur auswählen'); ed().open('commit', ids.join(',')); } },
  { id: 'group', label: 'Gruppe aus Auswahl', group: 'Spur', keys: ['mod+g'], run: () => {
    const ids = selectedTrackIds().filter((id) => st().project.tracks.some((t) => t.id === id));
    if (ids.length < 2) return toast('Erst mindestens zwei Spuren auswählen (⌘-Klick auf die Spurköpfe)');
    let gid: string | null = null;
    commit('Gruppe anlegen', (p) => { gid = mix.makeGroup(p, ids); });
    const g = st().project.groups?.find((x) => x.id === gid);
    if (g) { toast(`Gruppe „${g.name}“ mit ${ids.length} Spuren: Fader, Mute, Solo und Auswahl gemeinsam. Gruppenliste rechts.`); ed().setView({ side: 'groups' }); }
  } },
  { id: 'dupTracks', label: 'Spur duplizieren', group: 'Spur', keys: ['alt+shift+d'], run: () => {
    const ids = selectedTrackIds();
    if (!ids.length) return toast('Erst eine Spur auswählen (Klick auf den Spurkopf)');
    let made: string[] = [];
    commit(ids.length === 1 ? 'Spur duplizieren' : 'Spuren duplizieren', (p) => { made = op.duplicateTracks(p, ids, true); });
    st().select([], made[0] ?? null);
    toast(`${ids.length === 1 ? 'Spur' : `${ids.length} Spuren`} dupliziert, mit Clips, Plugins und Automation`);
  } },
  { id: 'deleteTracks', label: 'Spur löschen', group: 'Spur', run: () => {
    const ids = selectedTrackIds();
    if (!ids.length) return toast('Erst eine Spur auswählen (Klick auf den Spurkopf)');
    removeTracks(ids);
  } },
  { id: 'folder', label: 'Ausgewählte Spuren in Ordner', group: 'Spur', keys: ['alt+shift+f'], run: () => {
    const ids = selectedTrackIds();
    if (!ids.length) return toast('Erst eine oder mehrere Spuren auswählen (⌘-Klick auf die Spurköpfe)');
    const names = st().project.tracks.filter((t) => ids.includes(t.id)).map((t) => t.name);
    const guess = names.every((n) => /vox|vocal|gesang|chor|harm/i.test(n)) ? 'Vocals' : names.every((n) => /kick|snare|hat|drum|perc|tom|clap/i.test(n)) ? 'Drums' : 'Ordner';
    commit('Ordner anlegen', (p) => { op.makeFolder(p, ids, guess); });
    toast(`${ids.length} ${ids.length === 1 ? 'Spur' : 'Spuren'} im Ordner „${guess}“. Name per Klick ändern.`);
  } },
  { id: 'autoMaster', label: 'Master-Automation zeigen (z. B. Fade-out)', group: 'Automation', keys: ['alt+shift+m'], run: () => {
    const used = Object.entries(st().project.masterAutomation?.lanes ?? {}).filter(([, l]) => l.length).map(([k]) => k);
    ed().set({ autoLanes: { ...ed().autoLanes, master: (ed().autoLanes.master?.length ?? 0) ? [] : used.length ? used : ['volume'] } });
  } },
  { id: 'autoAllRead', label: 'Alle Spuren auf Read', group: 'Automation', run: () => commit('Automation: Read', (p) => { for (const t of p.tracks) if (t.automation) t.automation.mode = 'read'; if (p.masterAutomation) p.masterAutomation.mode = 'read'; }) },
  { id: 'autoAllOff', label: 'Alle Spuren auf Off', group: 'Automation', run: () => commit('Automation: Off', (p) => { for (const t of p.tracks) if (t.automation) t.automation.mode = 'off'; if (p.masterAutomation) p.masterAutomation.mode = 'off'; }) },
  // Zeit und Marker
  { id: 'marker', label: 'Marker setzen', group: 'Marker und Zeit', keys: ['m', 'numpadenter'], run: addMarker },
  { id: 'markers', label: 'Memory Locations …', group: 'Marker und Zeit', keys: ['shift+m'], run: () => ed().open('markers') },
  { id: 'time', label: 'Takte einfügen oder entfernen …', group: 'Marker und Zeit', run: () => ed().open('time') },
  { id: 'tabNext', label: 'Zum nächsten Anschlag bzw. zur nächsten Clip-Grenze', group: 'Marker und Zeit', keys: ['tab'], run: () => tab(1) },
  { id: 'tabPrev', label: 'Zum vorigen Anschlag bzw. zur vorigen Clip-Grenze', group: 'Marker und Zeit', keys: ['alt+tab'], run: () => tab(-1) },
  { id: 'tabExt', label: 'Auswahl bis zur nächsten Grenze', group: 'Marker und Zeit', keys: ['shift+tab'], run: () => tab(1, true) },
  // Ansicht
  { id: 'zoomIn', label: 'Hineinzoomen', group: 'Ansicht', keys: ['mod+]', '+'], run: () => st().setZoom(st().zoom * 1.25) },
  { id: 'zoomOut', label: 'Herauszoomen', group: 'Ansicht', keys: ['mod+[', '-'], run: () => st().setZoom(st().zoom / 1.25) },
  { id: 'zoomSel', label: 'Auf Auswahl zoomen', group: 'Ansicht', keys: ['e'], run: () => { const r = currentRange(); if (r && !op.isEmptyRange(r)) zoomTo(r.start, r.end); else zoomTo(0, projectLength(st().project)); } },
  { id: 'zoomAll', label: 'Ganzen Song zeigen', group: 'Ansicht', keys: ['alt+a'], run: () => zoomTo(0, projectLength(st().project) - 6) },
  { id: 'shortcuts', label: 'Alle Tastenkürzel', group: 'Ansicht', keys: ['shift+?'], run: () => ed().open('shortcuts') },
  // Modi und Werkzeuge
  { id: 'mShuffle', label: 'Shuffle', group: 'Edit-Modus', keys: ['f1', 'alt+1'], run: () => ed().set({ mode: 'shuffle' }) },
  { id: 'mSlip', label: 'Slip', group: 'Edit-Modus', keys: ['f2', 'alt+2'], run: () => ed().set({ mode: 'slip' }) },
  { id: 'mSpot', label: 'Spot', group: 'Edit-Modus', keys: ['f3', 'alt+3'], run: () => ed().set({ mode: 'spot' }) },
  { id: 'mGrid', label: 'Grid', group: 'Edit-Modus', keys: ['f4', 'alt+4'], run: () => ed().set({ mode: 'grid' }) },
  { id: 'snap', label: 'Snap to Grid (in Shuffle, Slip, Spot)', group: 'Edit-Modus', keys: ['shift+f4'], run: () => ed().set({ snap: !ed().snap }) },
  { id: 'cycleMode', label: 'Edit-Modus wechseln', group: 'Edit-Modus', keys: ['`', '^'], run: () => { const m: op.EditMode[] = ['shuffle', 'slip', 'spot', 'grid']; ed().set({ mode: m[(m.indexOf(ed().mode) + 1) % 4] }); } },
  { id: 'tSmart', label: 'Smart Tool', group: 'Werkzeug', keys: ['alt+5'], run: () => ed().set({ tool: 'smart' }) },
  { id: 'tTrim', label: 'Trim', group: 'Werkzeug', keys: ['f6', 'alt+6'], run: () => ed().set({ tool: 'trim' }) },
  { id: 'tSelect', label: 'Selector', group: 'Werkzeug', keys: ['f7', 'alt+7'], run: () => ed().set({ tool: 'select' }) },
  { id: 'tGrab', label: 'Grabber', group: 'Werkzeug', keys: ['f8', 'alt+8'], run: () => ed().set({ tool: 'grab' }) },
  { id: 'tScrub', label: 'Scrubber', group: 'Werkzeug', keys: ['f9'], run: () => ed().set({ tool: 'scrub' }) },
  { id: 'tPencil', label: 'Pencil', group: 'Werkzeug', keys: ['f10', 'alt+9'], run: () => ed().set({ tool: 'pencil' }) },
  { id: 'tCut', label: 'Schere (Klick schneidet, ⌥ auf allen Spuren)', group: 'Werkzeug', keys: ['shift+b'], run: () => ed().set({ tool: 'cut' }) },
  { id: 'tabTransient', label: 'Tab zum nächsten Anschlag an/aus', group: 'Marker und Zeit', run: () => { ed().set({ tabTransient: !ed().tabTransient }); toast(ed().tabTransient ? 'Tab springt zum nächsten Anschlag (Tab to Transient)' : 'Tab springt zur nächsten Clip-Grenze'); } },
];

function nudgeBy(dir: 1 | -1) {
  const p = st().project, d = dir * op.nudgeSec(p, ed().nudge, ed().range?.start ?? engine.position);
  const ids = st().selection.clipIds.length ? st().selection.clipIds : targetClips().map((c) => c.id);
  if (ids.length) {
    if (p.clips.some((c) => ids.includes(c.id) && c.locked)) toast('Gesperrte Clips bleiben stehen');
    st().commit('Nudge', (q) => op.nudge(q, ids, d), { merge: true });
    const r = ed().range;
    if (r) ed().setRange({ ...r, start: Math.max(0, r.start + d), end: Math.max(0, r.end + d) });
  } else {
    const r = ed().range;
    if (r) ed().setRange({ ...r, start: Math.max(0, r.start + d), end: Math.max(0, r.end + d) });
  }
}

export const command = (id: string) => COMMANDS.find((c) => c.id === id)!;
export const run = (id: string) => void command(id).run();

// ---------------------------------------------------------------------------
// Tastatur
function keyName(e: KeyboardEvent): string {
  if (e.code === 'NumpadEnter') return 'numpadenter';
  if (/^F\d+$/.test(e.key)) return e.key.toLowerCase();
  // Mit ⌥ liefert der Mac Sonderzeichen: dann die Taste über ihre Position bestimmen
  if (e.altKey && /^(Key[A-Z]|Digit\d)$/.test(e.code)) return e.code.slice(-1).toLowerCase();
  return e.key.length === 1 ? e.key.toLowerCase() : e.key.toLowerCase();
}
function parse(spec: string) {
  if (spec === '+') return { mods: [] as string[], key: '+' };
  const parts = spec.split('+');
  return { mods: parts.slice(0, -1), key: parts[parts.length - 1] };
}
function matches(spec: string, e: KeyboardEvent) {
  const { key } = parse(spec), mods = new Set(parse(spec).mods);
  // Mac: mod = ⌘, ctrl = ⌃. Windows/Linux: beide liegen auf Strg (wie die Start-Taste in Pro Tools nicht erreichbar ist)
  if (isMac) { if (mods.has('mod') !== e.metaKey || mods.has('ctrl') !== e.ctrlKey) return false; }
  else if ((mods.has('mod') || mods.has('ctrl')) !== e.ctrlKey) return false;
  if (mods.has('alt') !== e.altKey) return false;
  const k = keyName(e);
  // Umschalt nur prüfen, wenn sie nicht ohnehin das Zeichen erzeugt (z. B. "?" oder "+")
  if (/^[a-z0-9]$|^f\d+$|^arrow|^tab$|^backspace$|^delete$/.test(key) && mods.has('shift') !== e.shiftKey) return false;
  return k === key;
}
/** Kürzel lesbar machen (⌘ ⌥ ⌃ ⇧) */
export function keyLabel(spec?: string[]): string {
  if (!spec?.length) return '';
  const map: Record<string, string> = { mod: isMac ? '⌘' : 'Strg+', alt: isMac ? '⌥' : 'Alt+', ctrl: isMac ? '⌃' : 'Strg+', shift: isMac ? '⇧' : 'Umschalt+' };
  const { key, mods: m } = parse(spec[0]);
  const mods = m.map((x) => map[x] ?? x).join('');
  const names: Record<string, string> = { arrowup: '↑', arrowdown: '↓', backspace: '⌫', delete: 'Entf', tab: 'Tab', numpadenter: 'Enter (Ziffernblock)' };
  return mods + (names[key] ?? key.toUpperCase());
}

/** Edit-Kürzel ausführen. true, wenn etwas passiert ist. */
export function handleEditKey(e: KeyboardEvent): boolean {
  if (e.repeat && !/^(,|\.|\+|-)$/.test(e.key) && !e.key.startsWith('Arrow')) return false;
  for (const c of COMMANDS) {
    if (!c.keys?.some((k) => matches(k, e))) continue;
    e.preventDefault();
    void c.run();
    return true;
  }
  return false;
}

/** Takes löschen (mit den Clips, die aus ihnen stammen). Ein Rückgängig-Schritt. */
export function removeTakes(ids: string[], label: string) {
  let clips = 0;
  useStudio.getState().commit(ids.length === 1 ? 'Take löschen' : 'Takes löschen', (p) => { clips = op.deleteTakes(p, ids); });
  toast(`${label} gelöscht${clips ? `, ${clips} ${clips === 1 ? 'Clip' : 'Clips'} daraus aus der Timeline entfernt` : ''}. Rückgängig mit ⌘Z.`);
}

/** Ausgewählte Spuren: Spurauswahl im Edit-Bereich, sonst die zuletzt angeklickte Spur */
export function selectedTrackIds(): string[] {
  const r = currentRange(), sel = st().selection.trackId;
  return [...new Set([...(r?.trackIds ?? []).filter((x) => !x.includes(':')), ...(sel && !r?.trackIds.length ? [sel] : [])])];
}
/** Spuren löschen (mit Clips, Takes, Playlists). Ein Rückgängig-Schritt. */
export function removeTracks(ids: string[]) {
  const names = st().project.tracks.filter((t) => ids.includes(t.id)).map((t) => t.name);
  let r = { tracks: 0, clips: 0, takes: 0 };
  commit(ids.length === 1 ? `Spur „${names[0]}“ löschen` : `${ids.length} Spuren löschen`, (p) => { r = op.deleteTracks(p, ids); });
  // Ansicht aufräumen
  const e = ed(), cur = e.range;
  const autoLanes = { ...e.autoLanes }; for (const id of ids) delete autoLanes[id];
  e.set({ lanes: e.lanes.filter((x) => !ids.includes(x)), autoLanes });
  if (cur) { const left = cur.trackIds.filter((x) => !ids.some((id) => x === id || x.startsWith(`${id}:`))); e.setRange(left.length ? { ...cur, trackIds: left } : null); }
  if (ids.includes(st().selection.trackId ?? '')) st().select([], null);
  for (const id of ids) engine.laneOverride.delete(id);
  const what = [r.clips ? `${r.clips} ${r.clips === 1 ? 'Clip' : 'Clips'}` : '', r.takes ? `${r.takes} ${r.takes === 1 ? 'Take' : 'Takes'}` : ''].filter(Boolean).join(', ');
  toast(`${ids.length === 1 ? `Spur „${names[0]}“` : `${ids.length} Spuren`} gelöscht${what ? ` (mit ${what})` : ''}. Rückgängig mit ⌘Z.`);
}
